import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useDeliveries } from '@/hooks/useDeliveries'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { formatCurrency, formatRelativeTime } from '@/lib/utils'
import { Package, MapPin, ChevronRight, Loader2 } from 'lucide-react'
import type { DeliveryStatus } from '@/types'

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'pickup_ready', label: 'Ready' },
  { value: 'picked_up', label: 'Picked Up' },
  { value: 'in_transit', label: 'In Transit' },
  { value: 'delivered', label: 'Delivered' },
]

export default function DeliveriesPage() {
  const [filter, setFilter] = useState('')
  const { data: deliveries, isLoading, error } = useDeliveries(filter || undefined)

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Deliveries</h1>
        <p className="text-muted-foreground">Manage your delivery orders</p>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {STATUS_FILTERS.map(({ value, label }) => (
          <Button
            key={value}
            variant={filter === value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(value)}
            className="whitespace-nowrap"
          >
            {label}
          </Button>
        ))}
      </div>

      {/* Deliveries List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <Card>
          <CardContent className="py-12 text-center text-destructive">
            Failed to load deliveries. Please try again.
          </CardContent>
        </Card>
      ) : !deliveries?.length ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
            <p className="text-lg font-medium">No deliveries found</p>
            <p className="text-muted-foreground">
              {filter ? 'Try a different filter' : 'New orders will appear here'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {deliveries.map((delivery) => (
            <Link key={delivery.orderId} to={`/deliveries/${delivery.orderId}`}>
              <Card className="hover:bg-muted/50 transition-colors cursor-pointer">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold">#{delivery.orderNumber}</span>
                        <StatusBadge status={delivery.status} />
                      </div>
                      <div className="text-sm text-muted-foreground space-y-1">
                        <div className="flex items-center gap-1">
                          <span className="font-medium">From:</span> {delivery.pickup.chefName}
                        </div>
                        <div className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          <span className="truncate">{delivery.dropoff.address}, {delivery.dropoff.city}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-semibold text-lg">{formatCurrency(delivery.deliveryFee)}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatRelativeTime(delivery.createdAt)}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

function StatusBadge({ status }: { status: DeliveryStatus | string }) {
  const config: Record<string, { label: string; className: string }> = {
    pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-800' },
    assigned: { label: 'Assigned', className: 'bg-blue-100 text-blue-800' },
    pickup_ready: { label: 'Ready', className: 'bg-green-100 text-green-800' },
    picked_up: { label: 'Picked Up', className: 'bg-purple-100 text-purple-800' },
    in_transit: { label: 'In Transit', className: 'bg-indigo-100 text-indigo-800' },
    delivered: { label: 'Delivered', className: 'bg-green-100 text-green-800' },
    cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-800' },
  }

  const { label, className } = config[status] || { label: status, className: 'bg-gray-100 text-gray-800' }

  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${className}`}>
      {label}
    </span>
  )
}
